import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/apiClient";
import { Activity, Loader2, FileText, ShieldCheck } from "lucide-react";

export default function PublicReferral() {
  const { token } = useParams();
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/referrals/${token}`).then((r) => setRef(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (error || !ref) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Referral not found or link expired.</div>;

  const pdfUrl = `${API}/referrals/${ref.id}/pdf?origin=${encodeURIComponent(window.location.origin)}&token=${token}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-blue-600 text-white px-6 py-5">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2"><Activity className="w-6 h-6" strokeWidth={2.5} /><span className="text-xl font-bold font-head">ReferU</span></div>
          <span className="text-sm opacity-90">{ref.referral_number}</span>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Radiology Referral</span>
          <h1 className="text-2xl font-bold text-slate-900 font-head mt-2">{ref.patient?.first_name} {ref.patient?.last_name}</h1>
          <p className="text-slate-500 text-sm">{ref.patient?.gender} · {ref.patient?.age ? `${ref.patient.age} yrs` : ""} · {ref.created_at?.slice(0, 10)}</p>

          <div className="mt-6"><p className="text-xs font-semibold uppercase text-slate-400 mb-2">Examination requested</p>
            <div className="flex flex-wrap gap-2">{ref.studies?.map((s) => <span key={s.name} className="text-sm font-medium bg-blue-600 text-white px-3 py-1.5 rounded-full">{s.name}</span>)}</div>
            <p className="text-sm text-slate-600 mt-3">Priority: <b>{ref.priority}</b> · Contrast: <b>{ref.contrast}</b></p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mt-6">
            <div><p className="text-xs font-semibold uppercase text-slate-400 mb-2">Referring Doctor</p>
              <p className="text-sm font-medium text-slate-800">{ref.doctor_snapshot?.full_name}</p>
              <p className="text-sm text-slate-500">{ref.doctor_snapshot?.clinic_name}</p>
              <p className="text-sm text-slate-500">{ref.doctor_snapshot?.phone}</p>
            </div>
            {ref.centre_snapshot && (
              <div><p className="text-xs font-semibold uppercase text-slate-400 mb-2">Radiology Centre</p>
                <p className="text-sm font-medium text-slate-800">{ref.centre_snapshot.name}</p>
                <p className="text-sm text-slate-500">{ref.centre_snapshot.address}</p>
                <p className="text-sm text-slate-500">{ref.centre_snapshot.phone}</p>
              </div>
            )}
          </div>

          {ref.clinical?.clinical_history && (
            <div className="mt-6"><p className="text-xs font-semibold uppercase text-slate-400 mb-1">Clinical history</p><p className="text-sm text-slate-700">{ref.clinical.clinical_history}</p></div>
          )}

          <a href={pdfUrl} download target="_blank" rel="noreferrer" className="block">
            <button data-testid="public-download-pdf" className="w-full mt-8 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2"><FileText className="w-4 h-4" /> Download PDF</button>
          </a>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Generated securely by ReferU</p>
      </div>
    </div>
  );
}
